import { useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, ApolloError } from "@apollo/client";
import {
    getAreas,
    getFailureTypes,
    insertFailureType,
    insertFailureCode,
    updateFailureType,
    updateFailureCode,
    insertLocationSpecificFunctionalLocationFailureCodeRelation,
    deleteLocationSpecificFunctionalLocationFailureCodeRelation,
    getLocationSpecificFailureCodesByFunctionalLocationId,
    getNonLocationSpecificFailureCodesByMachineCenterId,
    insertNonLocationSpecificMachineCenterFailureCodeRelation,
    deleteNonLocationSpecificMachineCenterFailureCodeRelation,
    upsertArea,
    upsertFunctionalLocation,
    getFailureCodeUnknown,
    getDefaultFailureTypes,
    updateArea,
    updateFunctionalLocation,
    getExistingTranslation,
    updateTranslationNameMutation,
    insertTranslationNameMutation,
    setAreasOrderByMachineCenter,
    setFunctionalLocationsOrderByArea,
} from "api/queries/failureDetailsAPI";
import {
    IDefaultFailureType,
    IFailureCodeUnknown,
    IFailureDetailsEntity,
    IFuncionalLocationFailureCodesIds,
} from "./FailureDetailsEntityInterfaces";
import { IFailureType } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { IArea } from "./FailureDetailsEntityInterfaces";
import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { API } from "api/API";

export const FailureDetailsEntity: IFailureDetailsEntity = {
    GetAreas: (machine_center_id: number) => {
        const { loading, error, data, refetch } = useQuery(getAreas, {
            skip: !machine_center_id,
            variables: { machine_center_id },
        });
        const [areas, setAreas] = useState<IArea[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const areasArray: IArea[] = data.area;
                setAreas(areasArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, areas, apolloQueryError, refetch, setAreas };
    },
    GetFailureTypes: () => {
        const { loading, error, data, refetch } = useQuery(getFailureTypes);
        const [failureTypes, setFailureTypes] = useState<IFailureType[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const failureTypesArray: IFailureType[] = data.failure_type;
                setFailureTypes(failureTypesArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, failureTypes, apolloQueryError, refetch };
    },
    InsertFailureType: () => {
        const [addFailureType, { error }] = useMutation(insertFailureType);

        return { apolloMutationError: error, addFailureType };
    },
    InsertFailureCode: () => {
        const [addFailureCode, { error }] = useMutation(insertFailureCode);

        return { apolloMutationError: error, addFailureCode };
    },
    UpdateFailureType: () => {
        const [updateFailureTypeData, { error }] = useMutation(updateFailureType);
        return { apolloMutationError: error, updateFailureTypeData };
    },
    UpdateFailureCode: () => {
        const [updateFailureCodeData, { error }] = useMutation(updateFailureCode);
        return { apolloMutationError: error, updateFailureCodeData };
    },
    GetLocationSpecificFailureCodesByFunctionalLocationId: (functional_location_id: number) => {
        const { loading, error, data, refetch } = useQuery(getLocationSpecificFailureCodesByFunctionalLocationId, {
            variables: { functional_location_id },
        });
        const [locationSpecificFailureCodesIds, setLocationSpecificFailureCodesIds] = useState<
            IFuncionalLocationFailureCodesIds[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const failureCodesIdsArray: IFuncionalLocationFailureCodesIds[] =
                    data.location_specific_functional_location_failure_code;
                setLocationSpecificFailureCodesIds(failureCodesIdsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, locationSpecificFailureCodesIds, apolloQueryError, refetch };
    },
    InsertLocationSpecificFunctionalLocationFailureCodeRelation: () => {
        const [addLocationSpecificFunctionalLocationFailureCodeRelation, { error }] = useMutation(
            insertLocationSpecificFunctionalLocationFailureCodeRelation,
        );

        return { apolloMutationError: error, addLocationSpecificFunctionalLocationFailureCodeRelation };
    },
    DeleteLocationSpecificFunctionalLocationFailureCodeRelation: () => {
        const [removeLocationSpecificFunctionalLocationFailureCodeRelation, { error }] = useMutation(
            deleteLocationSpecificFunctionalLocationFailureCodeRelation,
        );

        return { apolloMutationError: error, removeLocationSpecificFunctionalLocationFailureCodeRelation };
    },
    GetNonLocationSpecificFailureCodesByMachineCenterId: (machine_center_id: number) => {
        const { loading, error, data, refetch } = useQuery(getNonLocationSpecificFailureCodesByMachineCenterId, {
            variables: { machine_center_id },
        });
        const [nonLocationSpecificFailureCodesIds, setNonLocationSpecificFailureCodesIds] = useState<
            IFuncionalLocationFailureCodesIds[]
        >([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const failureCodesIdsArray: IFuncionalLocationFailureCodesIds[] = [];

                for (const item in data) {
                    data[item].map((item: IFuncionalLocationFailureCodesIds) => failureCodesIdsArray.push(item));
                }

                setNonLocationSpecificFailureCodesIds(failureCodesIdsArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, nonLocationSpecificFailureCodesIds, apolloQueryError, refetch };
    },
    InsertNonLocationSpecificMachineCenterFailureCodeRelation: () => {
        const [addNonLocationSpecificMachineCenterFailureCodeRelation, { error }] = useMutation(
            insertNonLocationSpecificMachineCenterFailureCodeRelation,
        );

        return { apolloMutationError: error, addNonLocationSpecificMachineCenterFailureCodeRelation };
    },
    DeleteNonLocationSpecificMachineCenterFailureCodeRelation: () => {
        const [removeNonLocationSpecificMachineCenterFailureCodeRelation, { error }] = useMutation(
            deleteNonLocationSpecificMachineCenterFailureCodeRelation,
        );

        return { apolloMutationError: error, removeNonLocationSpecificMachineCenterFailureCodeRelation };
    },
    UpsertArea: () => {
        const [addOrInsertArea, { error }] = useMutation(upsertArea);
        return { apolloMutationError: error, addOrInsertArea };
    },
    UpsertFunctionalLocation: () => {
        const [addOrInsertFunctionalLocation, { error }] = useMutation(upsertFunctionalLocation);
        return { apolloMutationError: error, addOrInsertFunctionalLocation };
    },
    UpdateArea: () => {
        const [updateAreaData, { error }] = useMutation(updateArea);
        return { apolloMutationError: error, updateAreaData };
    },
    UpdateFunctionalLocation: () => {
        const [updateFunctionalLocationData, { error }] = useMutation(updateFunctionalLocation);
        return { apolloMutationError: error, updateFunctionalLocationData };
    },
    GetUnknownFailureCode: () => {
        const { loading, error, data } = useQuery(getFailureCodeUnknown);
        const [failureCodeUnknown, setFailureCodeUnknown] = useState<IFailureCodeUnknown[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const failureCodeUnknownArray: IFailureCodeUnknown[] = data.failure_code;
                setFailureCodeUnknown(failureCodeUnknownArray);
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, failureCodeUnknown, apolloQueryError };
    },
    GetDefaultFailureTypes: () => {
        const { loading, error, data } = useQuery(getDefaultFailureTypes);
        const [defaultFailureTypes, setDefaultFailureTypes] = useState<IDefaultFailureType[]>([]);
        const [apolloQueryError, setApolloQueryError] = useState<ApolloError | undefined>(undefined);

        useEffect(() => {
            if (data) {
                const defaultFailureTypes: IDefaultFailureType[] = data.default_failure_types;
                setDefaultFailureTypes(
                    defaultFailureTypes.filter(
                        (failureType) =>
                            failureType.code !== FAILURE_TYPES_CODE_EXCEPTIONS.BELOW_TRIGGER &&
                            failureType.code !== FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
                    ),
                );
            }
            if (error) {
                setApolloQueryError(error);
            }
        }, [data, error]);

        return { loading, defaultFailureTypes, apolloQueryError };
    },
    CheckForExistingTranslationId: () => {
        const getExistingTranslationId = useCallback(
            async ({
                name,
                corporate_id,
                language_id,
            }: {
                name: string;
                corporate_id: number;
                language_id: number;
            }) => {
                try {
                    const apolloClient = new API().client;
                    const response = await apolloClient.query({
                        query: getExistingTranslation,
                        variables: {
                            where: {
                                name: { _eq: name },
                                corporate_id: { _eq: corporate_id },
                                language_id: { _eq: language_id },
                            },
                        },
                    });
                    const translationId: number | undefined =
                        response?.data?.translation_name?.[0]?.translation_id || undefined;
                    return translationId;
                } catch (error) {
                    return undefined;
                }
            },
            [],
        );

        return { getExistingTranslationId };
    },
    UpdateTranslationName: () => {
        const [updateTranslatioName, { error }] = useMutation(updateTranslationNameMutation);
        return { apolloMutationError: error, updateTranslatioName };
    },
    InsertTranslationName: () => {
        const [insertTranslatioName, { error }] = useMutation(insertTranslationNameMutation);
        return { apolloMutationError: error, insertTranslatioName };
    },
    SetAreasOrderByMachineCenter: () => {
        const [setAreasOrder, { error, loading }] = useMutation(setAreasOrderByMachineCenter);
        return { apolloMutationError: error, setAreasOrder, loadingOrderUpdate: loading };
    },
    SetFunctionalLocationsOrderByArea: () => {
        const [setFunctionalLocationsOrder, { error, loading }] = useMutation(setFunctionalLocationsOrderByArea);
        return { apolloMutationError: error, setFunctionalLocationsOrder, loadingOrderUpdate: loading };
    },
};
